import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Modal, Spin, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import ConsentPanel from "../../components/delivery/ConsentPanel";
import ExpiredPanel from "../../components/delivery/ExpiredPanel";
import ErrorPanel from "../../components/delivery/ErrorPanel";
import ExpiryCountdown from "../../components/delivery/ExpiryCountdown";
import VideoReviewNameGate from "../../components/videoReview/VideoReviewNameGate";
import VideoReviewPlayer from "../../components/videoReview/VideoReviewPlayer";
import VideoReviewComments from "../../components/videoReview/VideoReviewComments";
import { useVideoReviewPayload } from "../../hooks/useVideoReviewPayload";
import {
    getCommentVisibility,
    getConsentClauseIds,
    getReviewerName,
    getVideoName,
    getVideoPath,
    hasConsent,
    isExpired,
    setConsent,
    setReviewerName,
} from "../../utils/videoReviewToken";
import { normalizeAlbumPath, toDeliveryPublicUrl } from "../../utils/deliveryPath";
import { getProxyDownloadUrl } from "../../utils/deliveryApi";
import { isStreamingDownloadSupported, streamResponseToFile } from "../../utils/deliveryDownload";
import { useTranslation } from "../../i18n";
import "../delivery/delivery.css";
import "./videoReview.css";

function sanitizeDownloadFilename(name) {
    const cleaned = String(name || "video")
        .replace(/[^\w\u4e00-\u9fff.\- ()]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .trim();

    return (cleaned || "video").slice(0, 100);
}

function getPathExtension(path) {
    const cleanPath = String(path || "").split("?")[0];
    const filename = cleanPath.slice(cleanPath.lastIndexOf("/") + 1);
    const match = filename.match(/\.([a-z0-9]{2,8})$/i);
    return match ? `.${match[1]}` : ".mp4";
}

function buildVideoDownloadName(videoName, videoPath) {
    const safeName = sanitizeDownloadFilename(videoName);
    if (/\.[a-z0-9]{2,8}$/i.test(safeName)) {
        return safeName;
    }
    return `${safeName}${getPathExtension(videoPath)}`;
}

function triggerBlobDownload(blob, filename) {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename || "video.mp4";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

function getVideoRootPrefix(videoPath) {
    const normalizedPath = normalizeAlbumPath(videoPath);
    const root = normalizedPath.split("/")[0] || "";
    return root || null;
}

async function fetchVideoResponse(url) {
    const response = await fetch(url, { cache: "no-store" });
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }
    return response;
}

async function fetchVideoResponseWithProxyFallback(primaryUrl, proxyUrl) {
    try {
        return await fetchVideoResponse(primaryUrl);
    } catch (directError) {
        if (!proxyUrl) {
            throw directError;
        }
        return fetchVideoResponse(proxyUrl);
    }
}

function VideoReview() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const playerRef = useRef(null);
    const composerInputRef = useRef(null);

    const handleVideoPause = useCallback(() => {
        composerInputRef.current?.focus();
    }, []);

    const { status, payload, reload } = useVideoReviewPayload(token);
    const [consented, setConsented] = useState(false);
    const [expired, setExpired] = useState(false);
    const [reviewerName, setReviewerNameState] = useState("");
    const [currentTimeMs, setCurrentTimeMs] = useState(0);
    const [downloadConfirmOpen, setDownloadConfirmOpen] = useState(false);
    const [downloadingVideo, setDownloadingVideo] = useState(false);

    useEffect(() => {
        if (!payload) {
            setConsented(false);
            setExpired(false);
            return;
        }
        setExpired(isExpired(payload.e));
        setConsented(getConsentClauseIds(payload).length === 0 || hasConsent(token));
        setReviewerNameState(getReviewerName(token));
    }, [payload, token]);

    const videoPath = useMemo(() => getVideoPath(payload), [payload]);
    const videoUrl = useMemo(() => (videoPath ? toDeliveryPublicUrl(videoPath) : null), [videoPath]);
    const proxyVideoUrl = useMemo(() => {
        if (!videoPath) {
            return null;
        }
        return getProxyDownloadUrl(videoPath, getVideoRootPrefix(videoPath));
    }, [videoPath]);

    const displayName = useMemo(
        () => getVideoName(payload, t("videoReview.gallery.defaultVideoName")),
        [payload, t]
    );

    const handleAgree = useCallback(() => {
        setConsent(token);
        setConsented(true);
    }, [token]);

    const handleLeave = useCallback(() => {
        navigate("/");
    }, [navigate]);

    const handleExpired = useCallback(() => {
        setExpired(true);
        setConsented(false);
    }, []);

    const handleNameSubmit = useCallback(
        (name) => {
            setReviewerName(token, name);
            setReviewerNameState(name);
        },
        [token]
    );

    const startVideoDownload = useCallback(
        async () => {
            if (!videoUrl) {
                return;
            }

            const filename = buildVideoDownloadName(displayName, videoPath);
            const response = await fetchVideoResponseWithProxyFallback(videoUrl, proxyVideoUrl);
            if (isStreamingDownloadSupported()) {
                await streamResponseToFile(response, filename);
                return;
            }

            triggerBlobDownload(await response.blob(), filename);
        },
        [displayName, proxyVideoUrl, videoPath, videoUrl]
    );

    const handleDownloadVideo = useCallback(async () => {
        if (!videoUrl || downloadingVideo) {
            return;
        }
        setDownloadConfirmOpen(true);
    }, [downloadingVideo, videoUrl]);

    const handleDownloadConfirm = useCallback(async () => {
        if (!videoUrl || downloadingVideo) {
            return;
        }

        const hide = message.loading(`${t("videoReview.download.downloading")}…`, 0);
        setDownloadingVideo(true);
        try {
            await startVideoDownload();
            message.success(t("videoReview.download.started"));
            setDownloadConfirmOpen(false);
        } catch (err) {
            if (err?.name !== "AbortError") {
                message.error(err.message || t("videoReview.download.failed"));
            }
        } finally {
            hide();
            setDownloadingVideo(false);
        }
    }, [downloadingVideo, startVideoDownload, t, videoUrl]);

    if (status === "loading") {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div style={{ textAlign: "center", padding: "3rem 0" }}>
                    <Spin size="large" />
                </div>
                <Footer />
            </>
        );
    }

    if (status === "expired" || expired) {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <ExpiredPanel />
                <Footer />
            </>
        );
    }

    if (status === "invalid") {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <ErrorPanel message={t("videoReview.gallery.notFound")} />
                <Footer />
            </>
        );
    }

    if (status === "error") {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <ErrorPanel message={t("videoReview.gallery.loadFailed")} onRetry={reload} />
                <Footer />
            </>
        );
    }

    if (!consented) {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <ConsentPanel
                        key={token}
                        consentNs="videoReview.consent"
                        expiryUtcSeconds={payload.e}
                        clauseIds={getConsentClauseIds(payload)}
                        onAgree={handleAgree}
                        onLeave={handleLeave}
                        onExpired={handleExpired}
                    />
                </div>
                <Footer />
            </>
        );
    }

    if (!reviewerName) {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <VideoReviewNameGate videoName={displayName} onSubmit={handleNameSubmit} />
                <Footer />
            </>
        );
    }

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <div className="video-review-page">
                <div className="video-review-page__title">
                    <h2>{displayName}</h2>
                    <ExpiryCountdown expiryUtcSeconds={payload.e} onExpired={handleExpired} />
                </div>

                <div className="video-review-layout">
                    <div className="video-review-layout__player">
                        {videoUrl ? (
                            <VideoReviewPlayer
                                ref={playerRef}
                                src={videoUrl}
                                videoName={displayName}
                                onTimeUpdate={setCurrentTimeMs}
                                onPause={handleVideoPause}
                            />
                        ) : (
                            <ErrorPanel message={t("videoReview.gallery.videoMissing")} />
                        )}
                        {videoUrl && (
                            <div className="video-review-download">
                                <Button
                                    type="primary"
                                    icon={<DownloadOutlined />}
                                    onClick={handleDownloadVideo}
                                    loading={downloadingVideo && !downloadConfirmOpen}
                                    block
                                >
                                    {t("videoReview.download.button")}
                                </Button>
                            </div>
                        )}
                    </div>
                    <VideoReviewComments
                        linkId={payload.id}
                        reviewerName={reviewerName}
                        playerRef={playerRef}
                        currentTimeMs={currentTimeMs}
                        commentVisibility={getCommentVisibility(payload)}
                        composerInputRef={composerInputRef}
                    />
                </div>
            </div>
            <Modal
                open={downloadConfirmOpen}
                title={t("videoReview.download.confirmTitle")}
                okText={t("videoReview.download.confirmOk")}
                cancelText={t("delivery.actions.cancel")}
                okButtonProps={{ loading: downloadingVideo }}
                onOk={handleDownloadConfirm}
                onCancel={() => setDownloadConfirmOpen(false)}
                destroyOnClose
            >
                <p>{t("videoReview.download.confirmBody")}</p>
            </Modal>
            <Footer />
        </>
    );
}

export default VideoReview;
